import { assign, fromPromise, sendTo, setup } from 'xstate';
import { sendAxiosErrorAlert } from '~/features/utils';
import { getPendingRequestConflict } from '~/features/utils/pendingRequestConflict';
import {
  CONFIRMATION_VIEW_STATE,
  PROVIDER_ASSOCIATION_STATE,
  USER_STATE,
} from '~/features/xstateSystem';
import {
  AssociationHistoryEntry,
  ManagedInvitationResult,
  NpiMatch,
  ProviderEmployerAssociation,
  ProviderEmployerInvitation,
} from '~/types';
import {
  addManagedProvider,
  getAssociationHistory,
  getAssociations,
  getInvitations,
  inviteManagedProvider,
  lookupProviderByNpi,
  removeAssociation,
  resendInvitation,
  revokeInvitation,
  sendInvitation,
} from './api';
import { getManagedError } from './managedErrors';
import { NPI_ERROR_MESSAGE, npiErrorFor } from './npi';

// [MCV-1894] The Add Provider form. Category/type are null until chosen.
export type AddProviderForm = {
  prefixName: string;
  firstName: string;
  lastName: string;
  providerCategoryId: number | null;
  providerTypeId: number | null;
  npiNum: string;
  email: string;
  sendInvitation: boolean;
};

export const EMPTY_ADD_FORM: AddProviderForm = {
  prefixName: '',
  firstName: '',
  lastName: '',
  providerCategoryId: null,
  providerTypeId: null,
  npiNum: '',
  email: '',
  sendInvitation: true,
};

// The NPI the lookup was run for, so a stale answer (the user kept typing) is dropped.
export type AddNpiLookup = {
  npi: string;
  match: NpiMatch;
  clerkEmailHint: string | null;
  managedByYou: boolean;
};

export type AddConflict = {
  code: 'NPI_IN_USE' | 'EMAIL_IN_USE' | 'PENDING_REQUEST_EXISTS';
  existingProviderId: number | null;
};

export type ManagedInviteError =
  | 'PROVIDER_HAS_ACCOUNT'
  | 'EMAIL_IN_USE'
  | 'PENDING_REQUEST_EXISTS'
  | 'INVALID_EMAIL';

export type ManagedInviteDraft = {
  associationId: number;
  providerName: string;
  email: string;
  error: ManagedInviteError | null;
};

// What the list turns into a toast once a vault was created / an invite sent.
export type ToastNotice = {
  kind: 'added' | 'invited';
  providerId: number | null;
  providerName: string;
  // emailSent false = delivery failed, null = uncertain; the toast offers a resend.
  invitation: ManagedInvitationResult | null;
};

type Context = {
  employerId: number;
  associations: ProviderEmployerAssociation[];
  invitations: ProviderEmployerInvitation[];
  // [MCV-1927] Ended associations, for the Previous Providers tab.
  history: AssociationHistoryEntry[];
  // The history read failed on the last load; the tab offers a retry instead of "none yet".
  historyError: boolean;
  loading: boolean;
  modalOpen: boolean;
  inviteEmail: string;
  inviteValidationError: string;
  // [MCV-1814] The invitation a duplicate send collided with, while the modal
  // asks whether to resend it.
  pendingInvitationId: number | null;
  // [MCV-1894] Optional NPI on the invite form, with its lookup answer for the
  // non-blocking "Add instead" suggestion.
  inviteNpi: string;
  inviteNpiError: string;
  inviteNpiMatch: NpiMatch | null;
  addForm: AddProviderForm;
  addValidationError: string;
  addNpiError: string;
  // Counts rejected submits; the modal moves focus to the first invalid field on each.
  addSubmitFailures: number;
  // A websocket refresh arrived while the add/invite prompts were open; reload on return to the list.
  dataStale: boolean;
  npiLookup: AddNpiLookup | null;
  addConflict: AddConflict | null;
  managedInvite: ManagedInviteDraft | null;
  toastNotice: ToastNotice | null;
  tabIndex: number;
  lastFetchTime: number;
  cachedEmployerId: number;
};

type Events =
  | { type: 'ON_LOAD'; payload: { employerId: number } }
  | { type: 'ON_OPEN_MODAL' }
  | { type: 'ON_CLOSE_MODAL' }
  | { type: 'ON_SET_INVITE_EMAIL'; email: string }
  | { type: 'ON_SEND_INVITATION' }
  | { type: 'ON_SET_INVITE_NPI'; npi: string }
  | { type: 'ON_INVITE_NPI_BLUR' }
  | { type: 'ON_OPEN_ADD_MODAL'; prefill?: { email?: string; npiNum?: string } }
  | { type: 'ON_CLOSE_ADD_MODAL' }
  | {
      type: 'ON_SET_ADD_FIELD';
      field: keyof AddProviderForm;
      value: string | number | boolean | null;
    }
  | { type: 'ON_NPI_BLUR' }
  | { type: 'ON_SUBMIT_ADD' }
  | { type: 'ON_SWITCH_TO_INVITE' }
  | { type: 'ON_OPEN_MANAGED_INVITE'; associationId: number; providerName: string }
  | { type: 'ON_CLOSE_MANAGED_INVITE' }
  | { type: 'ON_SET_MANAGED_INVITE_EMAIL'; email: string }
  | { type: 'ON_SUBMIT_MANAGED_INVITE' }
  | { type: 'ON_SWITCH_MANAGED_TO_INVITE' }
  | { type: 'ON_TOAST_NOTICE_HANDLED' }
  | { type: 'ON_RESEND_PENDING' }
  | { type: 'ON_CANCEL_PENDING' }
  | { type: 'ON_RESEND_INVITATION'; invitationId: number }
  | { type: 'ON_REVOKE_INVITATION'; invitationId: number }
  | { type: 'ON_SEND_REVOKE_CONFIRMATION'; invitationId: number }
  | { type: 'ON_SEND_RESEND_CONFIRMATION'; invitationId: number }
  | {
      type: 'ON_SEND_REMOVE_ASSOCIATION_CONFIRMATION';
      associationId: number;
      providerName: string;
      // [MCV-1927] An unclaimed employer-managed vault: the dialog says "Remove", not "Disassociate".
      managed?: boolean;
    }
  | { type: 'ON_REMOVE_ASSOCIATION'; associationId: number; managed?: boolean }
  | { type: 'ON_SET_TAB_INDEX'; payload: { index: number } }
  | { type: 'ON_DATA_CHANGED' } // data changed server-side; reload when it is safe to
  | { type: 'ON_WEBSOCKET_MESSAGE' }; // New event for websocket messages

type InputArgs = { input: { context: Context; event: Events } };

const isValidInviteEmail = (email: string): boolean =>
  email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const optionalText = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * The first field that stops the Add form being submitted, with what to tell the user.
 * Checked in the order the form shows its fields (names, Email, category, type, NPI), so
 * the message and the focus land on the topmost problem.
 */
export const findAddFormError = (
  form: AddProviderForm
): { field: keyof AddProviderForm; message: string } | null => {
  if (form.firstName.trim() === '') {
    return { field: 'firstName', message: "Enter the provider's first name" };
  }
  if (form.lastName.trim() === '') {
    return { field: 'lastName', message: "Enter the provider's last name" };
  }
  if (form.email.trim() !== '' && !isValidInviteEmail(form.email.trim())) {
    return { field: 'email', message: 'Please enter a valid email address' };
  }
  if (form.providerCategoryId == null) {
    return { field: 'providerCategoryId', message: 'Select a provider category' };
  }
  if (form.providerTypeId == null) {
    return { field: 'providerTypeId', message: 'Select a provider type' };
  }
  if (npiErrorFor(form.npiNum) !== '') return { field: 'npiNum', message: NPI_ERROR_MESSAGE };
  return null;
};

/** The first thing wrong with the Add form, or '' when it can be submitted. */
export const validateAddForm = (form: AddProviderForm): string =>
  findAddFormError(form)?.message ?? '';

const API_CALLS = {
  loadData: async ({ input }: InputArgs) => {
    const employerId = input.context.employerId;
    // Previous Providers is secondary: a history failure must not block the main lists,
    // but it is recorded so the tab can say so instead of claiming there are none.
    let historyError = false;
    const [associations, invitations, history] = await Promise.all([
      getAssociations(employerId),
      getInvitations(employerId),
      getAssociationHistory(employerId).catch((): AssociationHistoryEntry[] => {
        historyError = true;
        return [];
      }),
    ]);
    return { associations, invitations, history, historyError };
  },
  sendInvite: async ({ input }: InputArgs) => {
    const email = input.context.inviteEmail.trim();
    const npiNum = optionalText(input.context.inviteNpi);
    return npiNum ? sendInvitation(email, npiNum) : sendInvitation(email);
  },
  lookupAddNpi: async ({ input }: InputArgs) => {
    const npi = input.context.addForm.npiNum.trim();
    return { npi, result: await lookupProviderByNpi(npi) };
  },
  lookupInviteNpi: async ({ input }: InputArgs) => {
    const npi = input.context.inviteNpi.trim();
    return { npi, result: await lookupProviderByNpi(npi) };
  },
  addManaged: async ({ input }: InputArgs) => {
    const { addForm } = input.context;
    const email = optionalText(addForm.email);
    return addManagedProvider({
      firstName: addForm.firstName.trim(),
      lastName: addForm.lastName.trim(),
      prefixName: optionalText(addForm.prefixName),
      providerCategoryId: addForm.providerCategoryId as number,
      providerTypeId: addForm.providerTypeId as number,
      npiNum: optionalText(addForm.npiNum),
      email,
      // The toggle only means something once there is an address to send to.
      sendInvitation: email != null && addForm.sendInvitation,
    });
  },
  inviteManaged: async ({ input }: InputArgs) => {
    const draft = input.context.managedInvite;
    if (draft == null) throw new Error('No managed provider selected');
    return inviteManagedProvider(draft.associationId, draft.email.trim());
  },
  revokeInvite: async ({ input }: InputArgs) => {
    const event = input.event as Extract<Events, { type: 'ON_REVOKE_INVITATION' }>;
    return revokeInvitation(input.context.employerId, event.invitationId);
  },
  resendInvite: async ({ input }: InputArgs) => {
    const event = input.event as Extract<Events, { type: 'ON_RESEND_INVITATION' }>;
    return resendInvitation(input.context.employerId, event.invitationId);
  },
  removeAssociation: async ({ input }: InputArgs) => {
    const event = input.event as Extract<Events, { type: 'ON_REMOVE_ASSOCIATION' }>;
    await removeAssociation(input.context.employerId, event.associationId);
    return { managed: event.managed === true };
  },
  resendPending: async ({ input }: InputArgs) => {
    const { employerId, pendingInvitationId } = input.context;
    if (pendingInvitationId == null) throw new Error('No pending invitation to resend');
    return resendInvitation(employerId, pendingInvitationId);
  },
};

// Shared transition lists; the actions they name are defined in setup() below.
const ADD_SUBMIT_TRANSITIONS = [
  { guard: 'addFormValid' as const, target: '$_ADDING_MANAGED_PROVIDER' },
  { actions: 'flagAddFormErrors' as const },
];

const INVITE_NPI_BLUR_TRANSITIONS = [
  { guard: 'inviteNpiBlank' as const, actions: 'clearInviteNpiLookup' as const },
  { guard: 'inviteNpiInvalid' as const, actions: 'flagInviteNpiInvalid' as const },
  { target: '$_LOOKING_UP_INVITE_NPI' },
];

const MANAGED_INVITE_SUBMIT_TRANSITIONS = [
  { guard: 'managedInviteEmailValid' as const, target: '$_SENDING_MANAGED_INVITE' },
  { actions: 'flagManagedInviteEmailInvalid' as const },
];

const INVITE_NPI_INVALID_TRANSITION = {
  guard: 'inviteNpiInvalid' as const,
  actions: 'showInviteNpiInvalid' as const,
};

export const providerAssociationMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    loadData: fromPromise(API_CALLS.loadData),
    sendInvite: fromPromise(API_CALLS.sendInvite),
    resendInvite: fromPromise(API_CALLS.resendInvite),
    resendPending: fromPromise(API_CALLS.resendPending),
    lookupAddNpi: fromPromise(API_CALLS.lookupAddNpi),
    lookupInviteNpi: fromPromise(API_CALLS.lookupInviteNpi),
    addManaged: fromPromise(API_CALLS.addManaged),
    inviteManaged: fromPromise(API_CALLS.inviteManaged),
    revokeInvite: fromPromise(API_CALLS.revokeInvite),
    removeAssociation: fromPromise(API_CALLS.removeAssociation),
  },
  guards: {
    emailIsValid: ({ context }) => {
      return isValidInviteEmail(context.inviteEmail.trim());
    },
    canSendInvite: ({ context }) => {
      return isValidInviteEmail(context.inviteEmail.trim()) && context.employerId > 0;
    },
    inviteNpiInvalid: ({ context }) => npiErrorFor(context.inviteNpi) !== '',
    inviteNpiBlank: ({ context }) => context.inviteNpi.trim() === '',
    addNpiBlank: ({ context }) => context.addForm.npiNum.trim() === '',
    addNpiInvalid: ({ context }) => npiErrorFor(context.addForm.npiNum) !== '',
    addNpiAlreadyLooked: ({ context }) =>
      context.npiLookup != null && context.npiLookup.npi === context.addForm.npiNum.trim(),
    dataStale: ({ context }) => context.dataStale,
    addFormValid: ({ context }) => validateAddForm(context.addForm) === '',
    managedInviteEmailValid: ({ context }) =>
      context.managedInvite != null && isValidInviteEmail(context.managedInvite.email.trim()),
    hasFreshData: ({ context, event }) => {
      const requestedEmployerId =
        event.type === 'ON_LOAD' ? event.payload.employerId : context.employerId;
      const now = Date.now();
      const timeSinceFetch = now - context.lastFetchTime;
      return timeSinceFetch < 5000 && context.cachedEmployerId === requestedEmployerId;
    },
  },
  actions: {
    openModal: assign({
      modalOpen: true,
      inviteEmail: '',
      inviteValidationError: '',
      pendingInvitationId: null,
      inviteNpi: '',
      inviteNpiError: '',
      inviteNpiMatch: null,
    }),
    closeModal: assign({
      modalOpen: false,
      inviteEmail: '',
      inviteValidationError: '',
      pendingInvitationId: null,
      inviteNpi: '',
      inviteNpiError: '',
      inviteNpiMatch: null,
    }),
    openAddModal: assign(({ event }) => ({
      addForm: {
        ...EMPTY_ADD_FORM,
        email: (event.type === 'ON_OPEN_ADD_MODAL' && event.prefill?.email) || '',
        npiNum: (event.type === 'ON_OPEN_ADD_MODAL' && event.prefill?.npiNum) || '',
      },
      addValidationError: '',
      addNpiError: '',
      npiLookup: null,
      addConflict: null,
    })),
    closeAddModal: assign({
      addForm: EMPTY_ADD_FORM,
      addValidationError: '',
      addNpiError: '',
      npiLookup: null,
      addConflict: null,
    }),
    // Add -> Invite handoff: the typed email and NPI carry over to the invite form.
    switchAddToInvite: assign(({ context }) => ({
      modalOpen: true,
      inviteEmail: context.addForm.email.trim(),
      inviteNpi: context.addForm.npiNum.trim(),
      inviteValidationError: '',
      inviteNpiError: '',
      inviteNpiMatch: null,
      pendingInvitationId: null,
      addForm: EMPTY_ADD_FORM,
      addValidationError: '',
      addNpiError: '',
      npiLookup: null,
      addConflict: null,
    })),
    // Managed row -> normal invite, when the email already belongs to an account.
    switchManagedToInvite: assign(({ context }) => ({
      modalOpen: true,
      inviteEmail: context.managedInvite?.email.trim() ?? '',
      inviteNpi: '',
      inviteValidationError: '',
      inviteNpiError: '',
      inviteNpiMatch: null,
      pendingInvitationId: null,
      managedInvite: null,
    })),
    showInviteNpiInvalid: assign({ inviteValidationError: NPI_ERROR_MESSAGE }),
    clearInviteNpiLookup: assign({ inviteNpiError: '', inviteNpiMatch: null }),
    flagInviteNpiInvalid: assign({ inviteNpiError: NPI_ERROR_MESSAGE, inviteNpiMatch: null }),
    flagManagedInviteEmailInvalid: assign({
      managedInvite: ({ context }) =>
        context.managedInvite
          ? { ...context.managedInvite, error: 'INVALID_EMAIL' as const }
          : null,
    }),
    flagAddFormErrors: assign({
      addValidationError: ({ context }) => validateAddForm(context.addForm),
      addNpiError: ({ context }) => npiErrorFor(context.addForm.npiNum),
      addSubmitFailures: ({ context }) => context.addSubmitFailures + 1,
    }),
    // Keep the open prompt, but remember the list needs a reload once it closes.
    markStale: assign({ dataStale: true, lastFetchTime: 0, cachedEmployerId: -1 }),
    // [MCV-1894] "Resend invitation" on the vault-created toast must work whatever the machine
    // is doing, so it runs beside the current state and then asks for a refresh.
    resendInBackground: ({ context, event, self, system }) => {
      if (event.type !== 'ON_RESEND_INVITATION') return;
      const notify = (payload: unknown) => system.get(USER_STATE)?.send(payload as never);
      resendInvitation(context.employerId, event.invitationId)
        .then(() =>
          notify({
            type: 'ON_SHOW_ALERT',
            payload: { text: 'Invitation resent', type: 'success', timeout: 4000 },
          })
        )
        .catch((error: unknown) => notify(sendAxiosErrorAlert({ error })))
        .finally(() => self.send({ type: 'ON_DATA_CHANGED' }));
    },
    setAddField: assign(({ context, event }) => {
      if (event.type !== 'ON_SET_ADD_FIELD') return {};
      const addForm = { ...context.addForm, [event.field]: event.value } as AddProviderForm;
      // A new category invalidates the chosen type.
      if (event.field === 'providerCategoryId') addForm.providerTypeId = null;
      return {
        addForm,
        addValidationError: '',
        addConflict: null,
        // Editing the NPI makes the previous lookup and NPI error stale.
        ...(event.field === 'npiNum' ? { addNpiError: '', npiLookup: null } : {}),
      };
    }),
    clearPending: assign({ pendingInvitationId: null }),
    setLoading: assign({ loading: true, dataStale: false }),
    clearLoading: assign({ loading: false }),
    openRevokeConfirmation: ({ system, event }) => {
      const { invitationId } = event as Extract<Events, { type: 'ON_SEND_REVOKE_CONFIRMATION' }>;
      system.get(CONFIRMATION_VIEW_STATE).send({
        type: 'ON_SHOW_CONFIRMATION',
        payload: {
          title: 'Revoke Invitation',
          message: 'Are you sure you want to revoke this invitation?',
          confirmLabel: 'Revoke',
          onConfirm: () => {
            system.get(PROVIDER_ASSOCIATION_STATE).send({
              type: 'ON_REVOKE_INVITATION',
              invitationId,
            });
          },
        },
      });
    },
    // [MCV-1826] Employer-initiated disassociation. Always confirmed: it ends the
    // employer's access to the provider's vault immediately.
    openRemoveAssociationConfirmation: ({ system, event }) => {
      const { associationId, providerName, managed } = event as Extract<
        Events,
        { type: 'ON_SEND_REMOVE_ASSOCIATION_CONFIRMATION' }
      >;
      system.get(CONFIRMATION_VIEW_STATE).send({
        type: 'ON_SHOW_CONFIRMATION',
        payload: managed
          ? {
              // [MCV-1927] The vault was created by the employer and never claimed.
              title: 'Remove provider?',
              message: `${providerName} hasn't accepted an invitation yet. Removing them archives the vault you created and cancels any pending invitation. You'll find them under Previous Providers.`,
              confirmLabel: 'Remove',
              onConfirm: () => {
                system.get(PROVIDER_ASSOCIATION_STATE).send({
                  type: 'ON_REMOVE_ASSOCIATION',
                  associationId,
                  managed: true,
                });
              },
            }
          : {
              title: 'Disassociate Provider',
              message: `Are you sure you want to disassociate ${providerName}? You will no longer be able to view or manage their vault.`,
              confirmLabel: 'Disassociate',
              onConfirm: () => {
                system.get(PROVIDER_ASSOCIATION_STATE).send({
                  type: 'ON_REMOVE_ASSOCIATION',
                  associationId,
                });
              },
            },
      });
    },
    openResendConfirmation: ({ system, event }) => {
      const { invitationId } = event as Extract<Events, { type: 'ON_SEND_RESEND_CONFIRMATION' }>;
      system.get(CONFIRMATION_VIEW_STATE).send({
        type: 'ON_SHOW_CONFIRMATION',
        payload: {
          title: 'Resend Invitation',
          message:
            'Are you sure you want to resend this provider association invitation? The previous link will stop working.',
          confirmLabel: 'Resend',
          onConfirm: () => {
            system.get(PROVIDER_ASSOCIATION_STATE).send({
              type: 'ON_RESEND_INVITATION',
              invitationId,
            });
          },
        },
      });
    },
    updateCacheTime: assign({
      lastFetchTime: () => Date.now(),
      cachedEmployerId: ({ context }) => context.employerId,
    }),
    clearCache: assign({
      lastFetchTime: 0,
      cachedEmployerId: -1,
    }),
  },
}).createMachine({
  id: 'providerAssociationState',
  context: ({ input }) => ({
    employerId: input?.employerId ?? -1,
    associations: [],
    invitations: [],
    history: [],
    historyError: false,
    loading: false,
    modalOpen: false,
    inviteEmail: '',
    inviteValidationError: '',
    pendingInvitationId: null,
    inviteNpi: '',
    inviteNpiError: '',
    inviteNpiMatch: null,
    addForm: EMPTY_ADD_FORM,
    addValidationError: '',
    addNpiError: '',
    addSubmitFailures: 0,
    dataStale: false,
    npiLookup: null,
    addConflict: null,
    managedInvite: null,
    toastNotice: null,
    tabIndex: 0,
    lastFetchTime: 0,
    cachedEmployerId: -1,
  }),
  initial: '$_IDLE',
  on: {
    ON_LOAD: [
      {
        guard: 'hasFreshData',
        target: '.$_VIEW', // Skip loading, go straight to view
        actions: assign({
          employerId: ({ event }) => event.payload.employerId,
        }),
      },
      {
        target: '.$_LOADING',
        actions: assign({ employerId: ({ event }) => event.payload.employerId }),
      },
    ],
    ON_REVOKE_INVITATION: { target: '.$_REVOKING_INVITE' },
    ON_OPEN_MODAL: { actions: 'openModal' },
    ON_CLOSE_MODAL: { actions: 'closeModal' },
    ON_SET_INVITE_EMAIL: {
      actions: assign({
        inviteEmail: ({ event }) => event.email,
        inviteValidationError: '',
      }),
    },
    ON_SET_INVITE_NPI: {
      actions: assign({
        inviteNpi: ({ event }) => event.npi,
        inviteNpiError: '',
        inviteNpiMatch: null,
        inviteValidationError: '',
      }),
    },
    ON_SET_ADD_FIELD: { actions: 'setAddField' },
    ON_OPEN_MANAGED_INVITE: {
      actions: assign({
        managedInvite: ({ event }) => ({
          associationId: event.associationId,
          providerName: event.providerName,
          email: '',
          error: null,
        }),
      }),
    },
    ON_CLOSE_MANAGED_INVITE: { actions: assign({ managedInvite: null }) },
    ON_SET_MANAGED_INVITE_EMAIL: {
      actions: assign({
        managedInvite: ({ context, event }) =>
          context.managedInvite
            ? { ...context.managedInvite, email: event.email, error: null }
            : null,
      }),
    },
    ON_SWITCH_MANAGED_TO_INVITE: { actions: 'switchManagedToInvite' },
    ON_TOAST_NOTICE_HANDLED: { actions: assign({ toastNotice: null }) },
    ON_SET_TAB_INDEX: {
      actions: assign({ tabIndex: ({ event }) => event.payload.index }),
    },
    ON_WEBSOCKET_MESSAGE: {
      target: '.$_LOADING',
      actions: 'clearCache',
    },
    // Reload without interrupting what is running: $_VIEW overrides this, any other state
    // remembers it and reloads on its way back to $_VIEW.
    ON_DATA_CHANGED: { actions: 'markStale' },
    // States that take a resend themselves ($_VIEW, $_LOADING) override this.
    ON_RESEND_INVITATION: { actions: 'resendInBackground' },
  },
  states: {
    $_IDLE: {
      on: {
        ON_SEND_INVITATION: [
          INVITE_NPI_INVALID_TRANSITION,
          {
            guard: 'canSendInvite',
            target: '$_SENDING_INVITE',
          },
          {
            guard: 'emailIsValid',
            actions: assign({
              inviteValidationError:
                'Employer data has not been loaded yet. Please wait for the page to load fully.',
            }),
          },
          {
            actions: assign({ inviteValidationError: 'Please enter a valid email address' }),
          },
        ],
      },
    },
    $_VIEW: {
      // Back on the list after a refresh was held back: load the fresh data.
      always: { guard: 'dataStale', target: '$_LOADING' },
      on: {
        ON_DATA_CHANGED: { target: '$_LOADING', actions: 'clearCache' },
        ON_SEND_INVITATION: [
          INVITE_NPI_INVALID_TRANSITION,
          {
            guard: 'canSendInvite',
            target: '$_SENDING_INVITE',
          },
          {
            guard: 'emailIsValid',
            actions: assign({
              inviteValidationError:
                'Unable to send invitation. Please reload the page and try again.',
            }),
          },
          {
            actions: assign({ inviteValidationError: 'Please enter a valid email address' }),
          },
        ],
        ON_RESEND_INVITATION: { target: '$_RESENDING_INVITE' },
        ON_OPEN_ADD_MODAL: { target: '$_ADD_MODAL_OPEN', actions: ['closeModal', 'openAddModal'] },
        ON_INVITE_NPI_BLUR: INVITE_NPI_BLUR_TRANSITIONS,
        ON_SUBMIT_MANAGED_INVITE: MANAGED_INVITE_SUBMIT_TRANSITIONS,
        // Only from $_VIEW, like resend: a repeat confirm during the request is ignored.
        ON_REMOVE_ASSOCIATION: { target: '$_REMOVING_ASSOCIATION' },
        ON_SEND_REVOKE_CONFIRMATION: { actions: 'openRevokeConfirmation' },
        ON_SEND_RESEND_CONFIRMATION: { actions: 'openResendConfirmation' },
        ON_SEND_REMOVE_ASSOCIATION_CONFIRMATION: { actions: 'openRemoveAssociationConfirmation' },
      },
    },
    $_LOADING: {
      entry: 'setLoading',
      on: {
        // The "resend" button on the vault-created toast can be pressed while the list reloads.
        ON_RESEND_INVITATION: { target: '$_RESENDING_INVITE' },
        ON_SEND_INVITATION: [
          INVITE_NPI_INVALID_TRANSITION,
          {
            guard: 'canSendInvite',
            target: '$_SENDING_INVITE',
          },
          {
            guard: 'emailIsValid',
            actions: assign({
              inviteValidationError:
                'Provider association data is still loading. Please try again in a moment.',
            }),
          },
          {
            actions: assign({ inviteValidationError: 'Please enter a valid email address' }),
          },
        ],
      },
      invoke: {
        src: 'loadData',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: [
            assign({
              associations: ({ event }) => event.output.associations,
              invitations: ({ event }) => event.output.invitations,
              history: ({ event }) => event.output.history,
              historyError: ({ event }) => event.output.historyError,
              loading: false,
            }),
            'updateCacheTime',
          ],
        },
        onError: {
          target: '$_VIEW',
          actions: [
            'clearLoading',
            // The whole load failed, so history was not read either: let the
            // Previous Providers tab offer a retry instead of "none yet".
            assign({ historyError: true }),
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },
    // [MCV-1894] Invite form NPI blur: ask whether any provider has this NPI so the modal can
    // offer "Add instead". Never blocks the send, which stays available while this runs.
    $_LOOKING_UP_INVITE_NPI: {
      invoke: {
        src: 'lookupInviteNpi',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: assign({
            // Drop an answer for an NPI the user has since edited.
            inviteNpiMatch: ({ context, event }) =>
              event.output.npi === context.inviteNpi.trim() ? event.output.result.match : null,
          }),
        },
        onError: { target: '$_VIEW', actions: assign({ inviteNpiMatch: null }) },
      },
      on: {
        // Closing the modal cancels the lookup instead of leaving the list stuck behind it.
        ON_CLOSE_MODAL: { target: '$_VIEW', actions: 'closeModal' },
        ON_SEND_INVITATION: [
          INVITE_NPI_INVALID_TRANSITION,
          { guard: 'canSendInvite', target: '$_SENDING_INVITE' },
          {
            guard: 'emailIsValid',
            actions: assign({
              inviteValidationError:
                'Unable to send invitation. Please reload the page and try again.',
            }),
          },
          { actions: assign({ inviteValidationError: 'Please enter a valid email address' }) },
        ],
        ON_OPEN_ADD_MODAL: { target: '$_ADD_MODAL_OPEN', actions: ['closeModal', 'openAddModal'] },
      },
    },
    // [MCV-1894] Add Provider modal. A websocket refresh must not tear it down.
    $_ADD_MODAL_OPEN: {
      on: {
        ON_NPI_BLUR: [
          {
            guard: 'addNpiBlank',
            actions: assign({ npiLookup: null, addNpiError: '' }),
          },
          {
            guard: 'addNpiInvalid',
            actions: assign({ npiLookup: null, addNpiError: NPI_ERROR_MESSAGE }),
          },
          { guard: 'addNpiAlreadyLooked' },
          { target: '$_LOOKING_UP_NPI' },
        ],
        ON_SUBMIT_ADD: ADD_SUBMIT_TRANSITIONS,
        ON_CLOSE_ADD_MODAL: { target: '$_VIEW', actions: 'closeAddModal' },
        ON_SWITCH_TO_INVITE: { target: '$_VIEW', actions: 'switchAddToInvite' },
        ON_WEBSOCKET_MESSAGE: { actions: 'markStale' },
      },
    },
    $_LOOKING_UP_NPI: {
      invoke: {
        src: 'lookupAddNpi',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_ADD_MODAL_OPEN',
          actions: assign({
            npiLookup: ({ context, event }) =>
              event.output.npi === context.addForm.npiNum.trim()
                ? {
                    npi: event.output.npi,
                    match: event.output.result.match,
                    clerkEmailHint: event.output.result.clerkEmailHint,
                    managedByYou: event.output.result.managedByYou,
                  }
                : null,
          }),
        },
        // The lookup is advisory: Add still validates the NPI server-side.
        onError: { target: '$_ADD_MODAL_OPEN', actions: assign({ npiLookup: null }) },
      },
      on: {
        // Blur fires before the click on Add, so Add must work while the lookup is in flight.
        ON_SUBMIT_ADD: ADD_SUBMIT_TRANSITIONS,
        ON_CLOSE_ADD_MODAL: { target: '$_VIEW', actions: 'closeAddModal' },
        ON_SWITCH_TO_INVITE: { target: '$_VIEW', actions: 'switchAddToInvite' },
        ON_WEBSOCKET_MESSAGE: { actions: 'markStale' },
      },
    },
    // Repeat submits are ignored: there is no ON_SUBMIT_ADD handler while this runs.
    $_ADDING_MANAGED_PROVIDER: {
      invoke: {
        src: 'addManaged',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'closeAddModal',
            'clearCache',
            assign({
              toastNotice: ({ event }) => {
                const { association, invitation } = event.output;
                const provider = association.provider;
                return {
                  kind: 'added' as const,
                  providerId: association.providerId ?? provider.id,
                  providerName:
                    [provider.prefixName, provider.firstName, provider.lastName]
                      .filter(Boolean)
                      .join(' ') || 'No Name Given',
                  invitation,
                };
              },
            }),
          ],
        },
        onError: [
          {
            // Nothing was created: keep the modal for the handoff / message.
            guard: ({ event }) => {
              const code = getManagedError(event.error)?.code;
              return (
                code === 'NPI_IN_USE' ||
                code === 'EMAIL_IN_USE' ||
                code === 'PENDING_REQUEST_EXISTS'
              );
            },
            target: '$_ADD_MODAL_OPEN',
            actions: assign({
              addConflict: ({ event }) => {
                const managed = getManagedError(event.error);
                return managed
                  ? {
                      code: managed.code as AddConflict['code'],
                      existingProviderId: managed.existingProviderId,
                    }
                  : null;
              },
            }),
          },
          {
            guard: ({ event }) => getManagedError(event.error)?.code === 'INVALID_NPI',
            target: '$_ADD_MODAL_OPEN',
            actions: assign({
              addNpiError: NPI_ERROR_MESSAGE,
              addSubmitFailures: ({ context }) => context.addSubmitFailures + 1,
            }),
          },
          {
            target: '$_ADD_MODAL_OPEN',
            actions: sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          },
        ],
      },
      on: { ON_WEBSOCKET_MESSAGE: { actions: 'markStale' } },
    },
    $_SENDING_MANAGED_INVITE: {
      invoke: {
        src: 'inviteManaged',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'clearCache',
            assign({
              toastNotice: ({ context, event }) => ({
                kind: 'invited' as const,
                providerId: null,
                providerName: context.managedInvite?.providerName ?? 'No Name Given',
                invitation: event.output.invitation,
              }),
              managedInvite: null,
            }),
          ],
        },
        onError: [
          {
            guard: ({ event }) => {
              const code = getManagedError(event.error)?.code;
              return (
                code === 'PROVIDER_HAS_ACCOUNT' ||
                code === 'EMAIL_IN_USE' ||
                code === 'PENDING_REQUEST_EXISTS'
              );
            },
            target: '$_VIEW',
            actions: assign({
              managedInvite: ({ context, event }) =>
                context.managedInvite
                  ? {
                      ...context.managedInvite,
                      error: getManagedError(event.error)?.code as ManagedInviteError,
                    }
                  : null,
            }),
          },
          {
            target: '$_LOADING',
            actions: [
              'clearCache',
              assign({ managedInvite: null }),
              sendTo(
                ({ system }) => system.get(USER_STATE),
                ({ event }) => sendAxiosErrorAlert(event)
              ),
            ],
          },
        ],
      },
      on: { ON_WEBSOCKET_MESSAGE: { actions: 'markStale' } },
    },
    $_SENDING_INVITE: {
      invoke: {
        src: 'sendInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'closeModal',
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              () => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: 'Invitation sent successfully',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: [
          {
            // [MCV-1814] A request to this email is already pending: nothing
            // was created or sent. Keep the modal open and ask whether to
            // resend that one.
            guard: ({ event }) => getPendingRequestConflict(event.error) !== null,
            target: '$_PENDING_EXISTS',
            actions: assign({
              pendingInvitationId: ({ event }) =>
                getPendingRequestConflict(event.error)?.existingId ?? null,
            }),
          },
          {
            // The API persists the pending invitation before attempting delivery.
            // A delivery error can therefore still leave a resendable invitation
            // on the server, so reload instead of returning to stale local data.
            target: '$_LOADING',
            actions: [
              'closeModal',
              'clearCache',
              sendTo(
                ({ system }) => system.get(USER_STATE),
                ({ event }) => sendAxiosErrorAlert(event)
              ),
            ],
          },
        ],
      },
    },
    // [MCV-1814] The modal shows "A pending request already exists for {email}.
    // Resend it?". Resend goes through the same endpoint as the row action;
    // Cancel returns to the form with the typed email intact.
    $_PENDING_EXISTS: {
      on: {
        ON_RESEND_PENDING: { target: '$_RESENDING_PENDING' },
        ON_CANCEL_PENDING: { target: '$_VIEW', actions: 'clearPending' },
        ON_CLOSE_MODAL: { target: '$_VIEW', actions: 'closeModal' },
      },
    },
    $_RESENDING_PENDING: {
      invoke: {
        src: 'resendPending',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'closeModal',
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              () => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: 'Request resent',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: {
          target: '$_LOADING',
          actions: [
            'closeModal',
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },
    $_REVOKING_INVITE: {
      invoke: {
        src: 'revokeInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              () => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: 'Invitation revoked',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: {
          // [MCV-1926] Reload: a 409 INVITATION_EXPIRED means the row is no longer PENDING.
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },
    $_REMOVING_ASSOCIATION: {
      invoke: {
        src: 'removeAssociation',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          // Reloads the list AND the Previous Providers history (loadData fetches both).
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: event.output.managed ? 'Provider removed' : 'Provider disassociated',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: {
          // Reload too: a 404 means the provider already left, and the stale row
          // would otherwise stay on screen.
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },
    $_RESENDING_INVITE: {
      // A resend is already running: a repeat (row or toast) is ignored, not run beside it.
      on: { ON_RESEND_INVITATION: {} },
      invoke: {
        src: 'resendInvite',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              () => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: 'Invitation resent',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: {
          // Reload authoritative data before re-enabling actions: a rotated
          // code may already have been committed server-side even though the
          // resend request itself errored out.
          target: '$_LOADING',
          actions: [
            'clearCache',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },
  },
});
